import { Fragment, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Package, Plus, Trash2 } from "lucide-react";
import { Combobox, type ComboboxOption } from "@wa/ui";
import { showVatOn } from "@wa/core/sales-vat";
import { useT } from "../../../../i18n/I18nProvider";
import { useInvoicingVat } from "../../../useInvoicingVat";
import {
  computeLineTotals,
  formatCents,
  getItems,
  type Item,
  type LineItem,
  type QtyMode,
} from "../../../../lib/sales";
import { Btn, EmptyState, IconBtn, PillTabs, listCard } from "../../../ui";
import { centsToInput, inputToCents } from "./money";
import { DocTotals } from "./DocTotals";

type T = ReturnType<typeof useT>;

/**
 * Description, quantity, unit, unit price, VAT, discount, line total and the
 * delete track. One constant for the head and every row, because a grid does
 * not complain when the two disagree - the numbers simply end up under the
 * wrong heading.
 *
 * This one stays a grid of cells rather than becoming a row of pills. A pill
 * row is a list of objects you click on; a document's lines are a matrix of
 * numbers you read down a column and compare, and six figures wrapped into a
 * pill cannot be compared at all.
 */
const COLS = "minmax(130px,2.4fr) 72px 82px 104px 66px 70px 100px 34px";

/** The same row without the VAT track. Not the grid above with an empty cell in
 *  it: an empty track still takes its 66px and reads as a column that failed to
 *  load. The width it gives up goes to the description. */
const COLS_NO_VAT = "minmax(130px,2.4fr) 72px 82px 104px 70px 100px 34px";

/** Below this the eight tracks stop fitting, so the card scrolls sideways
 *  instead of squeezing the price column into three characters. Below 560px it
 *  stops applying altogether and the line folds instead - see `.crm-line-grid`
 *  in index.css, which is why this is a variable and not an inline `minWidth`.
 *  Nothing can outrank an inline style. */
const MIN_W = 810 + 20;
/** One track narrower, so a no-VAT table starts scrolling that much later. */
const MIN_W_NO_VAT = MIN_W - 66;

const blankLine = (taxRatePct: number): LineItem => ({
  description: "", qty: 1, unit: "tk", unitPriceCents: 0, taxRatePct,
});

/**
 * The line's detail paragraph, under its name and inside the same cell.
 *
 * It lives in the description cell rather than in an eighth column because it
 * is the only field on the line that is prose: a column would have to be as
 * wide as all six figures put together and would still wrap. Underneath the
 * name it reads the way it prints - name first, explanation below - and the
 * grid keeps its tracks.
 *
 * The box grows with what is typed, up to a limit, so five bullet points do not
 * have to be written through a two-line slot and twenty do not push the price
 * off the screen.
 */
function DetailsField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="crm-inp crm-line-ta">
      <textarea
        rows={value ? Math.min(8, value.split("\n").length + 1) : 2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="text-[12.25px]"
      />
    </label>
  );
}

/** Common units, offered as datalist hints; the field stays free text. */
const UNIT_SUGGESTIONS = ["tk", "h", "päev", "kuu", "kord", "km", "m²"];

const QTY_MODES: QtyMode[] = ["qty", "hours", "qty_hours"];

/** The quantity column's label, used by the radio row AND the column head. */
export function qtyModeLabel(t: T, mode: QtyMode): string {
  return t(`sales.qtyMode.${mode === "qty_hours" ? "qtyHours" : mode}`);
}

/**
 * The same label, told where it may break.
 *
 * "Kogus/Tundi" does not fit a 72px column at this tracking, and left alone the
 * browser printed it straight over the heading beside it. Letting it wrap is
 * not enough either: Chrome offers no break after a solidus here, so it cut the
 * word at "KOGUS/TU". `<wbr>` puts the break where a reader would - "KOGUS/"
 * over "TUNDI", which is the two lines the PDF already prints.
 *
 * The plain string above stays for the pill tabs, which have room for it.
 */
export function qtyModeHead(t: T, mode: QtyMode): ReactNode {
  const [first = "", ...rest] = qtyModeLabel(t, mode).split("/");
  return (
    <>
      {first}
      {rest.map((part, i) => (
        <Fragment key={i}>
          /<wbr />
          {part}
        </Fragment>
      ))}
    </>
  );
}

/**
 * One cell of the line grid: the CRM's sunken pill, shrunk to a height that
 * lets eight of them share a row without the row turning into a paragraph.
 * `crm-inp` sits in the components layer, so these utilities win outright.
 */
function Cell({
  value,
  onChange,
  type,
  step,
  placeholder,
  list,
  numeric,
  caption,
}: {
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  placeholder?: string;
  list?: string;
  numeric?: boolean;
  /** The column's name, carried down to the field itself. Hidden while the
   *  column head is on screen and saying it already; shown once the line folds
   *  on a phone, where six unlabelled boxes are otherwise unanswerable. */
  caption: ReactNode;
}) {
  return (
    <span className="crm-line-cell">
      <span className="crm-line-cap">{caption}</span>
      <label className="crm-inp crm-line-fld">
        <input
          type={type ?? "text"}
          step={step}
          list={list}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className={`text-[12.75px]${numeric ? " crm-tnum" : ""}`}
        />
      </label>
    </span>
  );
}

export function LineItemsEditor({
  value,
  onChange,
  qtyMode = "qty",
  onQtyModeChange,
}: {
  value: LineItem[];
  onChange: (v: LineItem[]) => void;
  qtyMode?: QtyMode;
  onQtyModeChange?: (m: QtyMode) => void;
}) {
  const t = useT();
  const [items, setItems] = useState<Item[]>([]);
  const { vatEnabled, defaultTaxRatePct } = useInvoicingVat();
  // A document being edited is judged on the lines in front of the operator,
  // not on a banked total it does not have yet. So an old invoice reopened for
  // an edit keeps its column even after the company stops charging VAT, and a
  // new one written while the switch is off never grows one.
  const showVat = showVatOn({ vatEnabled, lines: value });
  const cols = showVat ? COLS : COLS_NO_VAT;
  const minW = showVat ? MIN_W : MIN_W_NO_VAT;

  useEffect(() => {
    getItems({ active: "true" }).then((r) => setItems(r.rows));
  }, []);

  const itemOptions = useMemo<ComboboxOption[]>(
    () => items.map((i) => ({ value: i.id, label: i.name, hint: formatCents(i.unitPriceCents) })),
    [items],
  );

  const totals = computeLineTotals(value);

  function update(idx: number, patch: Partial<LineItem>) {
    onChange(value.map((li, i) => (i === idx ? { ...li, ...patch } : li)));
  }
  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }
  function addBlank() {
    onChange([...value, blankLine(defaultTaxRatePct)]);
  }
  function addFromItem(itemId: string) {
    const it = items.find((i) => i.id === itemId);
    if (!it) return;
    onChange([
      ...value,
      {
        itemId: it.id,
        description: it.name,
        qty: 1,
        unit: it.unit ?? "tk",
        unitPriceCents: it.unitPriceCents,
        // A hidden column may not receive a value. An item template saved back
        // when the company charged VAT still says 24%, and copying that onto a
        // line the operator cannot see would put VAT on the invoice with
        // nothing on screen to explain where it came from.
        taxRatePct: showVat ? it.taxRatePct : defaultTaxRatePct,
      },
    ]);
  }

  return (
    <div className="space-y-3.5">
      {/* Unit hints (including "h" for hours); the field also takes free text. */}
      <datalist id="wa-line-units">
        {UNIT_SUGGESTIONS.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>

      {/* How the quantity is shown (Perfex calls it "Show quantity as") - it
          decides the column head here and the column head in the PDF. Three
          mutually exclusive choices, so it wears the CRM's pill tabs rather
          than three radio dots. */}
      {onQtyModeChange ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="crm-mut text-[12px] font-semibold">{t("sales.qtyMode.label")}</span>
          <PillTabs
            small
            value={qtyMode}
            onChange={onQtyModeChange}
            tabs={QTY_MODES.map((m) => ({ value: m, label: qtyModeLabel(t, m) }))}
          />
        </div>
      ) : null}

      <div className="crm-card crm-line-wrap overflow-x-auto">
        {value.length === 0 ? (
          // No minimum width on the empty state: there are no columns to hold
          // apart yet, and forcing one would hand an empty card a scrollbar.
          <EmptyState icon={Package} title={t("sales.line.empty")} />
        ) : (
          <div className="crm-line-grid" style={{ "--crm-line-min": `${minW}px` } as CSSProperties}>
            <div className="crm-list-head" style={{ ...listCard(cols), gridTemplateColumns: cols, paddingTop: 22 }}>
              <span>{t("sales.line.description")}</span>
              <span>{qtyModeHead(t, qtyMode)}</span>
              <span>{t("sales.line.unit")}</span>
              <span>{t("sales.line.unitPrice")}</span>
              {showVat ? <span>{t("sales.line.tax")}</span> : null}
              <span>{t("sales.line.discount")}</span>
              <span className="text-right">{t("sales.line.total")}</span>
              <span />
            </div>

            <div className="crm-line-list flex flex-col pb-3.5">
              {value.map((li, idx) => {
                // Per-line preview of what `computeLineTotals` will bank:
                // gross, less the discount, plus VAT on what is left.
                const gross = (li.qty || 0) * (li.unitPriceCents || 0);
                const discount = li.discountPct ? (gross * li.discountPct) / 100 : 0;
                const net = gross - discount;
                const lineTotal = net + (net * (li.taxRatePct ?? 0)) / 100;

                return (
                  <div
                    key={idx}
                    className="crm-grid-row crm-line-row"
                    style={{ "--crm-line-cols": cols } as CSSProperties}
                  >
                    <span className="crm-line-cell">
                      <span className="crm-line-cap">{t("sales.line.description")}</span>
                      <label className="crm-inp crm-line-fld">
                        <input
                          type="text"
                          value={li.description}
                          onChange={(e) => update(idx, { description: e.target.value })}
                          placeholder={t("sales.line.descPlaceholder")}
                          autoComplete="off"
                          className="text-[12.75px]"
                        />
                      </label>
                      <DetailsField
                        value={li.details ?? ""}
                        onChange={(v) => update(idx, { details: v || undefined })}
                        placeholder={t("sales.line.detailsPlaceholder")}
                      />
                    </span>
                    <Cell
                      value={li.qty}
                      onChange={(v) => update(idx, { qty: parseFloat(v) || 0 })}
                      type="number"
                      step="any"
                      numeric
                      caption={qtyModeHead(t, qtyMode)}
                    />
                    <Cell
                      value={li.unit ?? ""}
                      onChange={(v) => update(idx, { unit: v || undefined })}
                      list="wa-line-units"
                      placeholder={t("sales.line.unitPlaceholder")}
                      caption={t("sales.line.unit")}
                    />
                    <Cell
                      value={centsToInput(li.unitPriceCents)}
                      onChange={(v) => update(idx, { unitPriceCents: inputToCents(v) })}
                      type="number"
                      step="0.01"
                      numeric
                      caption={t("sales.line.unitPrice")}
                    />
                    {showVat ? (
                      <Cell
                        value={li.taxRatePct}
                        onChange={(v) => update(idx, { taxRatePct: parseInt(v) || 0 })}
                        type="number"
                        step="1"
                        numeric
                        caption={t("sales.line.tax")}
                      />
                    ) : null}
                    <Cell
                      value={li.discountPct ?? ""}
                      onChange={(v) => update(idx, { discountPct: v ? parseFloat(v) : undefined })}
                      type="number"
                      step="1"
                      placeholder="0"
                      numeric
                      caption={t("sales.line.discount")}
                    />

                    <span className="crm-line-cell crm-line-sum text-right">
                      <span className="crm-line-cap">{t("sales.line.total")}</span>
                      <span className="crm-tnum block text-[13.25px] font-bold">
                        {formatCents(Math.round(lineTotal))}
                      </span>
                    </span>

                    <IconBtn
                      small
                      onClick={() => remove(idx)}
                      title={t("common.delete")}
                      aria-label={t("common.delete")}
                      className="crm-line-del text-red-600 hover:text-red-600"
                    >
                      <Trash2 className="size-3.5" />
                    </IconBtn>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
          <Btn onClick={addBlank}>
            <Plus className="size-4" />
            {t("sales.line.add")}
          </Btn>
          <Package className="crm-mut size-3.5 shrink-0" />
          {/* `w-60` was a fixed 240px, which with the button and the icon came to
              390px - wider than a 375px phone. It keeps its width where there is
              room and gives it back where there is not. */}
          <div className="w-60 min-w-[150px] max-w-full flex-1">
            <Combobox
              options={itemOptions}
              onChange={addFromItem}
              value=""
              clearable={false}
              // Pill and fill come from the CRM's `combobox-trigger` rule; this
              // is the short-field measurement for a toolbar row.
              className="h-[38px] border-0 px-4 text-[12.75px] shadow-none"
              popoverClassName="rounded-2xl"
              placeholder={t("sales.line.fromItem")}
              searchPlaceholder={t("sales.searchItem")}
              emptyLabel={t("sales.noItems")}
            />
          </div>
        </div>

        <DocTotals
          t={t}
          showVat={showVat}
          subtotalCents={totals.subtotalCents}
          taxCents={totals.taxCents}
          totalCents={totals.totalCents}
        />
      </div>
    </div>
  );
}
